import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { DeleteClient, GetClient, GetClients } from "./ClientStore";
import { Modal, Button, Table } from "../components/Modal";
import { EditClient } from "./Edit";

export const GrantTypeMap = {
    authorization_code: "Authorization Code",
    implicit: "Implicit",
    client_credentials: "Client Credentials",
    hybrid: "Hybrid",
    "urn:ietf:params:oauth:grant-type:device_code": "Device Code",
    password: "Password",
};

const ClientsTable = ({ user, policies, organization, toggleCreateClientModal }) => {
    let hasPermission = (permission) => {
        if (user && policies) {
            if (user.claims.filter((c) => c.type == "http://schemas.microsoft.com/ws/2008/06/identity/claims/role" && c.value == "Admin").length > 0) {
                return true;
            } else {
                return policies.filter((policy) => policy.permission == permission).length > 0;
            }
        }
        return false;
    };

    const navigate = useNavigate();

    const [clients, setClients] = React.useState([]);
    const [showEditClientModal, setShowEditClientModal] = React.useState(false);
    const [currentClient, setCurrentClient] = React.useState(null);

    React.useEffect(() => {
        loadClients();
    }, [organization]);

    const loadClients = (client?) => {
        GetClients(organization.id).then((res) => {
            setClients(res.data);
            if (client) {
                setCurrentClient(client);
            }
        });
    };

    const [showDeleteClientModal, setShowDeleteClientModal] = React.useState(false);
    const [clientToDelete, setClientToDelete] = React.useState(null);

    const startDeleteClient = (id) => () => {
        setClientToDelete(id);
        setShowDeleteClientModal(true);
    };

    const deleteClient = (id) => {
        DeleteClient(id).then(() => {
            loadClients();
            setClientToDelete(null);
            setShowDeleteClientModal(false);
        });
    };

    const toggleEditClientModal = (id) => {
        if (showEditClientModal) {
            setShowEditClientModal(false);
            setCurrentClient(null);
        }

        if (!showEditClientModal && id !== null) {
            GetClient(id).then((response) => {
                setShowEditClientModal(true);
                setCurrentClient(response.data);
            });
        }
    };

    return (
        hasPermission("manageClients") && (
            <>
                {/* Header Section */}
                <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                    <FontAwesomeIcon icon={["fad", "sitemap"]} className="text-3xl text-purple-500" />
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-800">Clients</h2>
                        <p className="text-sm text-gray-600">Manage external applications' authentication</p>
                    </div>
                </div>
                <Table>
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grant Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {clients &&
                            clients.map((client, i) => (
                                <tr key={i} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{client.name}</div>
                                        <div className="text-sm text-gray-500">{client.id}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            {client.grantTypes.filter((g) => g == "authorization_code").length > 0 && client.requirePKCE && (
                                                <>
                                                    <FontAwesomeIcon icon={["fad", "lock"]} className="text-green-600 mr-1"></FontAwesomeIcon>
                                                </>
                                            )}
                                            {client.grantTypes.filter((g) => g == "authorization_code").length > 0 && !client.requirePKCE && (
                                                <>
                                                    <FontAwesomeIcon icon={["fad", "unlock"]} className="text-red-600 mr-1"></FontAwesomeIcon>
                                                </>
                                            )}
                                            <span className="text-sm text-gray-900">{client.grantTypes.map((g) => GrantTypeMap[g]).join(" | ")}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => {
                                                navigate(`/organizations/clients/${client.id}/permissions`);
                                            }}
                                            className="text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                                        >
                                            <FontAwesomeIcon icon={["fad", "users-cog"]}></FontAwesomeIcon>
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => {
                                                navigate(`/organizations/clients/${client.id}/secrets`);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={["fad", "key"]}></FontAwesomeIcon>
                                        </Button>
                                        <Button variant="outline-success" size="sm" onClick={() => toggleEditClientModal(client.id)}>
                                            <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>
                                        </Button>
                                        <Button variant="outline-danger" size="sm" onClick={startDeleteClient(client.id)}>
                                            <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        <tr>
                            <td colSpan={2} className="px-6 py-4">
                                <button className="text-blue-600 hover:text-blue-800 underline" onClick={toggleCreateClientModal}>
                                    Add new client
                                </button>
                            </td>
                            <td className="px-6 py-4">
                                <button
                                    className="text-blue-600 hover:text-blue-800 underline"
                                    onClick={() => {
                                        navigate(`/organizations/clients/manageExternal`);
                                    }}
                                >
                                    Manage external clients
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </Table>

                <Modal isOpen={showEditClientModal} onClose={() => toggleEditClientModal(null)} title="Edit Client" icon={["fad", "pencil"]} iconColor="text-blue-600" size="xl">
                    <div className="p-6">{currentClient && <EditClient clientId={currentClient.id} organization={organization.id} updated={loadClients}></EditClient>}</div>
                    <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                        <Button variant="secondary" onClick={() => toggleEditClientModal(null)}>
                            Done
                        </Button>
                    </div>
                </Modal>

                <Modal isOpen={showDeleteClientModal} onClose={() => setShowDeleteClientModal(false)} title="Delete Client" icon={["fad", "trash"]} iconColor="text-red-600">
                    <div className="p-6">
                        <p className="text-gray-700">Are you sure you want to delete this client? This is irreversible and all associated secrets will be deleted.</p>
                    </div>
                    <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                        <Button variant="secondary" onClick={() => setShowDeleteClientModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={() => deleteClient(clientToDelete)}>
                            Delete
                        </Button>
                    </div>
                </Modal>
            </>
        )
    );
};

export default ClientsTable;
